import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, RiskBar, Skel, Stat } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'

/**
 * AI-assisted compliance reporting and predictive remediation (part 65).
 *
 * The score and the estimated breach date are deterministic projections over the part-71
 * obligation register and its evidence. When a model is configured it writes only the Thai
 * explanation; a row whose model is "template" was built in Go and is labelled as such.
 *
 * This page never changes an obligation, attaches evidence, or applies a plan. A plan is a
 * proposal: the only decision here is a human approval, and approval is final.
 */

type RiskBand = 'low' | 'moderate' | 'high' | 'critical'
type RiskState = 'on_track' | 'due_soon' | 'overdue'
type PlanStatus = 'draft' | 'approved' | 'rejected'

interface Factor {
  code: string
  label: string
  points: number
}

interface Prediction {
  id: string
  obligation_id: string
  obligation_code: string
  obligation_title: string
  as_of: string
  risk_score: number
  risk_band: RiskBand
  state: RiskState
  days_until: number
  overdue_days: number
  passed_deadline_on?: string
  estimated_breach_on: string
  days_to_breach: number
  filed_count: number
  late_count: number
  on_time_bps: number
  avg_lateness_days: number
  factors: Factor[]
  narrative: string
  model: string
  prompt_version: string
  created_at: string
}

interface PlanStep {
  order: number
  action: string
  owner_role: string
  due_in_days: number
  evidence_required: string
}

interface Plan {
  id: string
  prediction_id: string
  obligation_id: string
  obligation_code: string
  obligation_title: string
  title: string
  summary: string
  steps: PlanStep[]
  owner_role: string
  target_on: string
  risk_before: number
  risk_after: number
  status: PlanStatus
  reviewed_by?: string
  reviewed_by_name?: string
  reviewed_at?: string
  created_at: string
}

interface ObligationOption {
  id: string
  code: string
  title: string
  authority: string
  cadence: string
  overdue: boolean
  days_overdue: number
  dormant: boolean
  rag: 'green' | 'amber' | 'red'
}

const bandLabel: Record<RiskBand, string> = {
  low: 'ต่ำ',
  moderate: 'ปานกลาง',
  high: 'สูง',
  critical: 'วิกฤต',
}

const bandTone: Record<RiskBand, string> = {
  low: 'sev-low',
  moderate: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const statusLabel: Record<PlanStatus, string> = {
  draft: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ตีกลับ',
}

const roleLabel: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้ทบทวน',
}

const cadenceLabel: Record<string, string> = {
  monthly: 'รายเดือน',
  quarterly: 'รายไตรมาส',
  semiannual: 'ครึ่งปี',
  annual: 'รายปี',
  event: 'ตามเหตุการณ์',
}

function BandBadge({ band }: { band: RiskBand }) {
  return (
    <span className={`sev ${bandTone[band]}`}>
      <i className="sev-dot" />
      {bandLabel[band]}
    </span>
  )
}

function StatusBadge({ status }: { status: PlanStatus }) {
  const tone = status === 'approved' ? 'sev-low' : status === 'rejected' ? 'sev-critical' : 'sev-medium'
  return (
    <span className={`sev ${tone}`}>
      <i className="sev-dot" />
      {statusLabel[status]}
    </span>
  )
}

function dueText(p: Prediction): string {
  if (p.state === 'overdue') return `เลยกำหนด ${p.overdue_days} วัน`
  if (p.days_until === 0) return 'ครบกำหนดวันนี้'
  if (p.days_until < 0) return `อยู่ในระยะผ่อนผัน ${-p.days_until} วัน`
  return `เหลือ ${p.days_until} วัน`
}

export function ComplianceAI() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const [obligationId, setObligationId] = useState('')
  const [openPrediction, setOpenPrediction] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  const canGenerate = me ? ['owner', 'admin', 'audit_manager', 'auditor'].includes(me.role) : false
  const canApprove = me ? ['owner', 'admin', 'audit_manager', 'reviewer'].includes(me.role) : false

  const obligations = useQuery({
    queryKey: ['compliance', 'obligations'],
    queryFn: () => api<ObligationOption[]>('/v1/compliance/obligations'),
    staleTime: 30_000,
  })
  const predictions = useQuery({
    queryKey: ['compliance', 'ai', 'predictions'],
    queryFn: () => api<Prediction[]>('/v1/compliance/ai/predictions?limit=40'),
    staleTime: 15_000,
  })
  const plans = useQuery({
    queryKey: ['compliance', 'ai', 'plans'],
    queryFn: () => api<Plan[]>('/v1/compliance/ai/plans?limit=40'),
    staleTime: 15_000,
  })

  const obligationRows = useMemo(() => obligations.data ?? [], [obligations.data])
  const predictionRows = predictions.data ?? []
  const planRows = plans.data ?? []

  useEffect(() => {
    if (obligationId !== '' || obligationRows.length === 0) return
    const first = obligationRows.find((row) => row.overdue) ?? obligationRows[0]
    setObligationId(first.id)
  }, [obligationId, obligationRows])

  useEffect(() => {
    if (openPrediction !== null || predictionRows.length === 0) return
    setOpenPrediction(predictionRows[0].id)
  }, [openPrediction, predictionRows])

  const selected = obligationRows.find((row) => row.id === obligationId)

  const generate = useMutation({
    mutationFn: () => apiWrite<{ prediction: Prediction; plan: Plan }>(
      '/v1/compliance/ai/predictions', 'POST', { obligation_id: obligationId },
    ),
    onSuccess: (result) => {
      setOpenPrediction(result.prediction.id)
      setNotice(result.prediction.model === 'template'
        ? 'สร้างการพยากรณ์จากแม่แบบกำหนดแน่นอนแล้ว — ระบบไม่ได้เรียกโมเดลภายนอก'
        : `สร้างการพยากรณ์ด้วย ${result.prediction.model} แล้ว — ต้องให้มนุษย์ตรวจสอบก่อนดำเนินการ`)
      void qc.invalidateQueries({ queryKey: ['compliance', 'ai'] })
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'สร้างการพยากรณ์ไม่สำเร็จ'),
  })

  const approve = useMutation({
    mutationFn: (planID: string) => apiWrite<Plan>(`/v1/compliance/ai/plans/${planID}/approve`, 'POST', {}),
    onSuccess: (plan) => {
      setNotice(`อนุมัติแผน ${plan.obligation_code} แล้ว — การอนุมัติมีผลครั้งเดียวและย้อนกลับไม่ได้`)
      void qc.invalidateQueries({ queryKey: ['compliance', 'ai'] })
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'อนุมัติแผนไม่สำเร็จ'),
  })

  const highRisk = predictionRows.filter((p) => p.risk_band === 'critical' || p.risk_band === 'high').length
  const draftPlans = planRows.filter((p) => p.status === 'draft').length
  const overdueDuties = obligationRows.filter((row) => row.overdue).length

  return (
    <>
      <PageHead
        title="AI ช่วยวิเคราะห์ compliance"
        sub="ประมาณการความเสี่ยง วันที่เสี่ยงถัดไป และร่างแผนแก้ไขจากทะเบียนภาระและประวัติหลักฐาน — มนุษย์เป็นผู้อนุมัติทุกครั้ง"
      />

      <div className="grid-4">
        <Stat label="การพยากรณ์ล่าสุด" value={predictionRows.length} unit="รายการ"
          foot="เก็บไว้เพื่อให้ตรวจย้อนกลับได้" />
        <Stat label="ความเสี่ยงสูงหรือวิกฤต" value={highRisk} unit="รายการ"
          foot="จากคะแนน 0 ถึง 100" />
        <Stat label="แผนรออนุมัติ" value={draftPlans} unit="แผน"
          foot="ยังไม่มีผลจนกว่ามนุษย์จะอนุมัติ" />
        <Stat label="ภาระที่เลยกำหนด" value={overdueDuties} unit="ภาระ"
          foot="อ้างอิงปฏิทินประเทศไทย" />
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">สร้างการพยากรณ์ใหม่</div>
        <div className="row" style={{ marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 320px', minWidth: 240 }}>
            <div className="dim" style={{ fontSize: 11, marginBottom: 6 }}>เลือกภาระจากทะเบียน</div>
            <select className="select" value={obligationId} onChange={(e) => setObligationId(e.target.value)}>
              {obligationRows.length === 0 ? <option value="">ยังไม่มีภาระในทะเบียน</option> : null}
              {obligationRows.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.code} · {row.title}
                  {row.overdue ? ` · เลยกำหนด ${row.days_overdue} วัน` : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-primary"
            disabled={!canGenerate || obligationId === '' || generate.isPending}
            onClick={() => { setNotice(''); generate.mutate() }}
          >
            {generate.isPending ? 'กำลังประเมิน…' : 'ประเมินความเสี่ยงและร่างแผน'}
          </button>
        </div>
        {selected ? (
          <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 10 }}>
            {selected.authority} · {cadenceLabel[selected.cadence] ?? selected.cadence}
            {selected.overdue ? ` · เลยกำหนด ${selected.days_overdue} วัน` : ''}
            {selected.dormant ? ' · ยังไม่มีกำหนดเปิด' : ''}
          </div>
        ) : null}
        {!canGenerate ? (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            บทบาทของคุณอ่านผลได้แต่ไม่มีสิทธิ์สร้างการพยากรณ์
          </div>
        ) : null}
        {notice ? (
          <div className="chip gold" style={{ marginTop: 12, whiteSpace: 'normal', lineHeight: 1.7 }}>{notice}</div>
        ) : null}
        <div className="dim" style={{ fontSize: 11, lineHeight: 1.8, marginTop: 12 }}>
          คะแนนคำนวณจากกำหนดตามปฏิทิน ความล่าช้าของหลักฐาน น้ำหนักของภาระ และระดับความสำคัญ
          ระบบไม่สรุปผลทางกฎหมาย และไม่เปลี่ยนสถานะของภาระหรือหลักฐานใด ๆ
        </div>
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">ผลการพยากรณ์</div>
          {predictions.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={120} /></div>
          ) : predictionRows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่มีการพยากรณ์" body="เลือกภาระแล้วกดประเมินเพื่อสร้างผลลัพธ์แรก ระบบจะเก็บวันที่และปัจจัยที่ใช้คำนวณไว้ด้วย" />
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              {predictionRows.map((p) => {
                const open = openPrediction === p.id
                return (
                  <div key={p.id} style={{ borderTop: '1px solid var(--color-hairline)', padding: '14px 0' }}>
                    <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>{p.obligation_title}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {p.obligation_code} · ข้อมูล ณ {thaiDate(p.as_of)} · {thaiDateTime(p.created_at)}
                        </div>
                      </div>
                      <BandBadge band={p.risk_band} />
                    </div>

                    <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 14 }}>
                      <RiskBar score={p.risk_score} />
                      <span className={`sev ${p.state === 'overdue' ? 'sev-critical' : p.state === 'due_soon' ? 'sev-high' : 'sev-low'}`}>
                        <i className="sev-dot" />
                        {dueText(p)}
                      </span>
                      <span className="chip">เสี่ยงถัดไป {thaiDate(p.estimated_breach_on)}</span>
                      <span className="chip">
                        {p.days_to_breach >= 0 ? `อีก ${p.days_to_breach} วัน` : `เลยกำหนด ${-p.days_to_breach} วัน`}
                      </span>
                    </div>

                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => setOpenPrediction(open ? null : p.id)}
                    >
                      {open ? 'ซ่อนรายละเอียด' : 'ดูปัจจัยและคำอธิบาย'}
                    </button>

                    {open ? (
                      <div style={{ marginTop: 8 }}>
                        <div className="grid-2" style={{ gap: 12 }}>
                          <div>
                            <div className="card-label" style={{ fontSize: 9.5 }}>ปัจจัยเสี่ยง</div>
                            <div style={{ marginTop: 8 }}>
                              {p.factors.length === 0 ? (
                                <div className="dim" style={{ fontSize: 11.5 }}>ไม่พบปัจจัยเสี่ยงที่มีนัยสำคัญ</div>
                              ) : p.factors.map((f) => (
                                <div key={f.code} className="row" style={{ justifyContent: 'space-between', padding: '5px 0' }}>
                                  <span style={{ fontSize: 11.5 }}>{f.label}</span>
                                  <span className="tnum dim" style={{ fontSize: 11 }}>+{f.points}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="card-label" style={{ fontSize: 9.5 }}>ประวัติหลักฐาน</div>
                            <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 8 }}>
                              {p.filed_count === 0 ? (
                                <>ยังไม่มีประวัติการยื่นในทะเบียน<br /></>
                              ) : (
                                <>
                                  ยื่นแล้ว {p.filed_count} งวด · ล่าช้า {p.late_count} งวด<br />
                                  ตรงเวลา {(p.on_time_bps / 100).toFixed(2)}% · ล่าช้าเฉลี่ย {p.avg_lateness_days} วัน<br />
                                </>
                              )}
                              {p.passed_deadline_on ? `กำหนดที่ผ่านมา ${thaiDate(p.passed_deadline_on)}` : 'ยังไม่เลยกำหนดเดิม'}
                            </div>
                          </div>
                        </div>
                        <div style={{
                          marginTop: 12, padding: '12px 14px', border: '1px solid var(--color-hairline)',
                          borderRadius: 10, whiteSpace: 'pre-wrap', fontSize: 11.8, lineHeight: 1.85,
                        }}>
                          {p.narrative}
                        </div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 8 }}>
                          แหล่งที่มา: {p.model === 'template' ? 'แม่แบบกำหนดแน่นอน (ไม่ได้เรียกโมเดล)' : p.model} · {p.prompt_version}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">แผนแก้ไขและสถานะการอนุมัติ</div>
          {plans.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={120} /></div>
          ) : planRows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่มีแผน" body="เมื่อสร้างการพยากรณ์ ระบบจะร่างแผนปฏิบัติการให้พร้อมกัน และรอการอนุมัติจากมนุษย์" />
            </div>
          ) : (
            <div style={{ marginTop: 6 }}>
              {planRows.map((plan) => (
                <div key={plan.id} style={{ borderTop: '1px solid var(--color-hairline)', padding: '14px 0' }}>
                  <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{plan.title}</div>
                      <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        เป้าหมาย {thaiDate(plan.target_on)} · ผู้รับผิดชอบ {roleLabel[plan.owner_role] ?? plan.owner_role}
                      </div>
                    </div>
                    <StatusBadge status={plan.status} />
                  </div>

                  <div className="row" style={{ marginTop: 8, gap: 10 }}>
                    <span className="chip">ความเสี่ยง {plan.risk_before} → {plan.risk_after}</span>
                    <span className="chip">{plan.steps.length} ขั้นตอน</span>
                  </div>

                  <div className="dim" style={{ fontSize: 11.4, lineHeight: 1.8, marginTop: 8 }}>{plan.summary}</div>

                  <ol style={{ margin: '10px 0 0 18px', padding: 0, fontSize: 11.5, lineHeight: 1.8 }}>
                    {plan.steps.map((step) => (
                      <li key={step.order} style={{ marginBottom: 6 }}>
                        {step.action}
                        <span className="dim"> · ภายใน {step.due_in_days} วัน · หลักฐาน: {step.evidence_required}</span>
                      </li>
                    ))}
                  </ol>

                  {plan.status === 'draft' ? (
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: 10 }}
                      disabled={!canApprove || approve.isPending}
                      onClick={() => { setNotice(''); approve.mutate(plan.id) }}
                    >
                      {approve.isPending ? 'กำลังบันทึก…' : 'อนุมัติแผน'}
                    </button>
                  ) : (
                    <div className="dim" style={{ fontSize: 10.5, marginTop: 10 }}>
                      {plan.reviewed_by_name || 'ผู้ทบทวน'} · {plan.reviewed_at ? thaiDateTime(plan.reviewed_at) : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dim" style={{ marginTop: 20, fontSize: 11, lineHeight: 1.8, maxWidth: 820 }}>
        ระบบแสดงวันที่คาดว่าจะถึงกำหนดเสี่ยงถัดไปจากปฏิทินของภาระและระยะผ่อนผันที่ลงทะเบียนไว้
        ไม่ใช่คำรับรองว่าจะเกิดการฝ่าฝืนหรือไม่เกิด และไม่ใช่ความเห็นทางกฎหมาย
        การอนุมัติแผนเป็นการตัดสินใจของมนุษย์และไม่เปลี่ยนสถานะภาระหรือหลักฐานในทะเบียน
      </div>
    </>
  )
}
